import React, { useState, useEffect } from 'react';
import api, { isSilentNetworkError } from '../../services/api';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import styles from './CategoryManagement.module.css';
import { PlusIcon, TagIcon, BookIcon } from '../../components/Icons';

function CategoryManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    feature_order: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/categories/');
      setCategories(response.data);
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('获取分类列表失败:', error);
      alert('获取分类列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    setFormData({ name: '', feature_order: 0 });
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, feature_order: category.feature_order ?? 0 });
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    if (window.confirm(`确定要删除分类 "${category.name}" 吗？`)) {
      try {
        await api.delete(`/categories/${category.id}`);
        fetchCategories();
        alert('分类删除成功');
      } catch (error) {
        if (isSilentNetworkError(error)) return;
        console.error('删除分类失败:', error);
        alert('删除分类失败');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('请输入分类名称');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        feature_order: Number.isFinite(Number(formData.feature_order))
          ? Number(formData.feature_order)
          : 0,
      };

      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, payload);
        alert('分类更新成功');
      } else {
        await api.post('/categories/', payload);
        alert('分类创建成功');
      }
      
      setShowModal(false);
      fetchCategories();
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('保存分类失败:', error);
      alert('保存分类失败');
    }
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px' },
    { key: 'name', label: '分类名称' },
    {
      key: 'feature_order',
      label: '排序值',
      width: '100px',
    },
    { 
      key: 'novels_count', 
      label: '小说数量', 
      render: (value, row) => (
        <span className={styles.count}>
          {row.novels?.length || 0} 本
        </span>
      )
    }
  ];

  return (
    <div className={styles.categoryManagement}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>分类管理</h1>
          <p className={styles.subtitle}>管理小说分类信息</p>
        </div>
        <button className={styles.createBtn} onClick={handleCreate}>
          <PlusIcon size={18} />
          添加分类
        </button>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <TagIcon size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{categories.length}</div>
            <div className={styles.statLabel}>总分类数</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <BookIcon size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>
              {categories.reduce((sum, cat) => sum + (cat.novels?.length || 0), 0)}
            </div>
            <div className={styles.statLabel}>关联小说</div>
          </div>
        </div>
      </div>

      <AdminTable
        data={categories}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="暂无分类数据"
      />

      {showModal && (
        <AdminModal
          title={editingCategory ? '编辑分类' : '添加分类'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          size="small"
        >
          <div className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>分类名称</label>
              <input
                type="text"
                className={styles.input}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="请输入分类名称"
                required
                autoFocus
              />
              <div className={styles.inputHint}>
                例如：玄幻、都市、言情、武侠等
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>推荐排序值</label>
              <input
                type="number"
                className={styles.input}
                value={formData.feature_order}
                onChange={(e) => setFormData({ ...formData, feature_order: e.target.value })}
                placeholder="数值越小排序越靠前"
                min={0}
              />
              <div className={styles.inputHint}>
                用于控制前台显示顺序，数值越小越靠前，默认 0
              </div>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default CategoryManagement;
